import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import GroupsTable from '@Components/GroupsTable';
import {
  ConfirmationMessageModal,
  MenuItem,
  ScrollToTop,
  TableActionsMenu,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import GroupsModal from '@Containers/Groups/GroupsModal';
import { GroupsQuery } from '@Models/groups.models';
import {
  useDeleteGroupMutation,
  useDeleteGroupsMutation,
  useGetGroupsListQuery,
} from '@Services/groups.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
} from '@Typings/global.types';
import {
  getConfirmationMessageNode,
  getModalBtnComponent,
  GetRows,
  getRTKQuery,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const AdminGroupsContainer: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [groupId, setGroupId] = useState<string>('');
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const groupsQuery: GroupsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  const {
    data,
    isLoading,
    isFetching,
    error: getGroupsError,
  } = useGetGroupsListQuery({
    data: groupsQuery,
  });
  const [deleteGroup, deleteGroupMethods] = useDeleteGroupMutation();
  const [deleteGroups, deleteGroupsMethods] = useDeleteGroupsMutation();

  const loading =
    isLoading ||
    isFetching ||
    deleteGroupMethods.isLoading ||
    deleteGroupsMethods.isLoading;

  const actionsComponent = (id: string, disabled?: boolean): JSX.Element => {
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setGroupId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setGroupId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setGroupId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
      isAdmin: true,
      hideEndIcon: true,
    });
  };

  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent, true);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  const handleBulk = (count: number) => setItemsCount(count);

  const handleAddGroup = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setGroupId('');
  };

  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? deleteGroups(checked) : deleteGroup(groupId);
    }
  };

  useEffect(() => {
    setGroupId('');
    setIsBulkAction(false);
    setItemsCount(0);
    if (deleteGroupMethods.isSuccess || deleteGroupsMethods.isSuccess) {
      setChecked([]);
    }
  }, [deleteGroupMethods.isSuccess, deleteGroupsMethods.isSuccess]);

  useEffect(() => {
    if (getGroupsError) {
      triggerAlert(getGroupsError, setAlertState);
    }
  }, [getGroupsError, triggerAlert]);

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <GroupsModal
        groupId={groupId}
        setGroupId={setGroupId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <GroupsTable
        isAdmin
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        alertState={alertState}
        selectedBtnNumber={itemsCount || checked.length}
        groupsAddBtnProps={{
          onClick: handleAddGroup,
        }}
        deleteBtnProps={{
          onClick: () => {
            setConfirmationActionType(ConfirmationActionTypes.Delete);
            setIsConfirmationModalOpen(true);
            setIsBulkAction(true);
          },
          disabled: loading,
        }}
        handleBulk={handleBulk}
      />
    </>
  );
};

export default AdminGroupsContainer;
